import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  ApiError,
  api,
  can,
  refusalReason,
  shortDate,
  tierLabel,
  type Campaign,
  type CampaignRecipient,
  type Vendor,
} from '../lib/api'
import { STATUS_WORDS, TASK_VERB } from '../lib/status'
import { notEmailed, whyNotEmailed } from '../lib/mail'
import { AssessTabs } from '../components/assess/AssessTabs'
import { DataTable, ErrorBox, ListCount, Loading, type Column } from '../components/DataTable'
import {
  MiniTrack,
  PageTabList,
  PageTabPanel,
  Status,
  Toggletip,
  pickerMatches,
  type StageState,
  type Tone,
} from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import { DateRuleNote, DateWarnNote } from '../components/DateRuleNote'
import { checkDueAfterToday, dueWarning, todayIndia, useLeftFields } from '../lib/dateRules'
import { mayPick, needsReason, pickNeedsReason } from '../lib/requestRules'
import '../styles/sweep.css'

// A campaign is the only way an assessment reaches a vendor, and mail to three
// hundred vendors is an outward-facing commercial act. So the screen is built
// around the holding state rather than around the create button: what is
// waiting on a person is the first thing on the page, and creating one says
// out loud that it sends nothing.
//
// Every status is a kit label and every campaign carries the small track of
// where it is, so the page reads as labels and counts; the reasons sit behind
// an (i) one press away rather than under every row.

// The questionnaires a campaign may send are the published ones the list
// offers, which leaves an archived one out (review 3.2). These two shipped
// sets stand in only while that read has not answered or could not be made;
// the server refuses an archived questionnaire whichever list offered it.
const QUESTIONNAIRES = [
  { code: 'TPRM-BASELINE', name: 'Baseline third party assessment' },
  { code: 'TPRM-REGULATED-IN', name: 'Regulated entity assessment (India)' },
]

const RISK_MODELS = [
  { code: 'BASELINE', note: 'Standard tiering, weights and gates.' },
  { code: 'REGULATED-IN', note: 'Regulated thresholds, with the gates that block approval rather than warn.' },
]

const MODES = [
  {
    code: 'FULL_CARRY_FORWARD',
    label: 'Full, carry forward',
    note: 'Every section is asked again, with last cycle answers pre-filled for the vendor to confirm or change. This is the normal reassessment.',
  },
  {
    code: 'FULL',
    label: 'Full',
    note: 'Every section is asked again from empty. Nothing is carried, so nothing is inherited from a prior answer.',
  },
  {
    code: 'DELTA',
    label: 'Delta',
    note:
      'Only the sections a trigger moved. The affected sections differ per vendor, so the scheduler raises a delta one vendor at a time. ' +
      'A delta assembled by hand here carries one section list for everybody, which is not the same thing, and is refused.',
  },
]

// Stored ordinals. They are what the vendor rows carry and what the chips
// filter on; tierLabel is what the chips print, because the ordinal runs the
// other way round from the name and a chip reading Tier 4 would gather the
// vendors a reader thinks it leaves out.
const TIERS = [1, 2, 3, 4]

export function modeLabel(code: string): string {
  return MODES.find((m) => m.code === code)?.label ?? code.replace(/_/g, ' ')
}

type StatusKey = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'SENDING' | 'SENT' | 'PARTIALLY_FAILED' | 'CANCELLED'

// The holding states are the work on this screen, so they carry the warning
// tone; a sent or cancelled campaign is finished and goes quiet. `stages` is
// where the status puts the campaign on its three stages (drafted, approved,
// sent), and nothing else: draft and pending approval both sit at approval,
// because either can be approved from the campaign page, and a partial
// failure blocks the send rather than finishing it.
const STATUS: Record<
  StatusKey,
  { label: string; tone: Tone; meaning: string; holding: boolean; stages: StageState[]; track: string }
> = {
  // A draft and a pending send both wait for a second person, and either can
  // be approved from the campaign page, so both carry the vocabulary's one
  // phrase for it (lib/status.ts) rather than "Draft" and "Needs approval".
  DRAFT: {
    label: STATUS_WORDS.campaign.DRAFT!.word,
    tone: 'warn',
    meaning: 'Assembled and holding, no assessment issued and no mail queued',
    holding: true,
    stages: ['done', 'current', 'upcoming'],
    track: 'Drafted, waiting for approval, then sent',
  },
  PENDING_APPROVAL: {
    label: STATUS_WORDS.campaign.PENDING_APPROVAL!.word,
    tone: 'warn',
    meaning: 'Waiting on a second person, no assessment issued and no mail queued',
    holding: true,
    stages: ['done', 'current', 'upcoming'],
    track: 'Drafted, waiting for approval, then sent',
  },
  APPROVED: {
    label: 'Approved',
    tone: 'info',
    meaning: 'A second person approved it, assessments issued and invitations queued',
    holding: false,
    stages: ['done', 'done', 'current'],
    track: 'Approved, invitations going out',
  },
  SENDING: {
    label: 'Sending',
    tone: 'info',
    meaning: 'The worker is draining the outbox for this campaign',
    holding: false,
    stages: ['done', 'done', 'current'],
    track: 'Approved, invitations going out',
  },
  SENT: {
    label: 'Sent',
    tone: 'plain',
    meaning: 'Every invitation left the outbox',
    holding: false,
    stages: ['done', 'done', 'done'],
    track: 'Drafted, approved and sent',
  },
  PARTIALLY_FAILED: {
    label: 'Partly failed',
    tone: 'crit',
    meaning: 'Some invitations did not send, the recipient list names which',
    holding: false,
    stages: ['done', 'done', 'blocked'],
    track: 'Approved, some invitations did not send',
  },
  CANCELLED: {
    label: 'Cancelled',
    tone: 'plain',
    meaning: 'Stopped before anything was issued',
    holding: false,
    stages: ['done', 'rejected', 'upcoming'],
    track: 'Cancelled before approval',
  },
}

export const CAMPAIGN_STAGE_NAMES = ['Drafted', 'Approved', 'Sent'] as const

// WHETHER THE INVITATIONS WERE EMAILED
//
// An approved campaign on an installation whose mail is muted, or in a
// workspace that switched its mail off, issues its assessments and emails
// nothing. The status still says APPROVED, and the worker records each
// invitation as FAILED with a reason saying it was not emailed, which the API
// reports as the recipient's mail, MUTED or OFF. This page used to show that
// as "Failed" in red under a campaign whose invitations were "queued" and
// whose third stage was "Sent". So the words come from what became of the
// invitations: recorded and not emailed, all of them or some.
//
// issued counts the invitations the approval issued; notEmailed those
// recorded and not emailed, together with the ones the worker has not
// reached when what it will do with them now is not to email them; mail is
// the reason, MUTED or OFF.
export type MailSummary = { issued: number; notEmailed: number; mail?: string }

export type MailOutcome = 'none' | 'some' | 'all'

export function mailOutcome(m: MailSummary | null | undefined): MailOutcome {
  if (!m || m.issued <= 0 || m.notEmailed <= 0) return 'none'
  return m.notEmailed >= m.issued ? 'all' : 'some'
}

function waiting(r: CampaignRecipient): boolean {
  return !!r.assessmentId && (r.deliveryState === 'PENDING' || r.deliveryState === 'SENDING')
}

// From the recipients the detail endpoint answers with, and its mail (what
// the worker will do now with the ones it has not reached).
export function summariseRecipients(recipients: CampaignRecipient[], mailNow?: string | null): MailSummary {
  const issued = recipients.filter((r) => r.assessmentId).length
  const recorded = recipients.filter((r) => notEmailed(r.mail))
  const pending = notEmailed(mailNow) ? recipients.filter((r) => waiting(r) && !r.mail).length : 0
  return { issued, notEmailed: recorded.length + pending, mail: recorded[0]?.mail ?? (pending > 0 ? mailNow ?? undefined : undefined) }
}

// From the counts the list endpoint answers with, and its mail.
export function summariseCounts(c: Campaign, mailNow?: string | null): MailSummary {
  const pending = notEmailed(mailNow) ? c.queued ?? 0 : 0
  // The list does not say which reason each recorded one had; the reason
  // shown is what the worker will do now, when that is not to email.
  return { issued: c.issued ?? 0, notEmailed: (c.notEmailed ?? 0) + pending, mail: notEmailed(mailNow) ? mailNow ?? undefined : undefined }
}

// Where the status puts a campaign on its stages, or null for a status this
// screen does not know, which then shows its word and no track. An approved
// campaign whose invitations were not emailed does not show them as going out
// or sent: its third stage is blocked, and says why.
export function campaignStages(
  status: string | undefined | null,
  mail?: MailSummary | null,
): { states: StageState[]; label: string } | null {
  const k = statusKey(status)
  if (!k) return null
  const outcome = mailOutcome(mail)
  if (outcome !== 'none' && (k === 'APPROVED' || k === 'SENDING' || k === 'SENT')) {
    return {
      states: ['done', 'done', 'blocked'],
      label: outcome === 'all' ? 'Approved and issued, recorded, not emailed' : 'Approved and issued, some invitations not emailed',
    }
  }
  return { states: STATUS[k].stages, label: STATUS[k].track }
}

// A status the screen does not know is still printed, in sentence case.
function statusWords(code: string | undefined | null): string {
  return (code || 'Unknown').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())
}

// Rank puts the two states that need a person at the top of a descending sort.
// A campaign nobody has decided on is the only row on this screen that is
// actually work.
const STATUS_RANK: Record<string, number> = {
  PENDING_APPROVAL: 7,
  DRAFT: 6,
  PARTIALLY_FAILED: 5,
  APPROVED: 4,
  SENDING: 3,
  SENT: 2,
  CANCELLED: 1,
}

function statusKey(status: string | undefined | null): StatusKey | null {
  if (!status) return null
  const k = status.trim().toUpperCase().replace(/[\s-]+/g, '_')
  return k in STATUS ? (k as StatusKey) : null
}

export function isHolding(status: string | undefined | null): boolean {
  const k = statusKey(status)
  return k ? STATUS[k].holding : false
}

// Status carries the whole meaning of this screen, so it is the kit label: the
// word, a dot and a colour, and the word is always there, so nobody has to be
// able to see the colour. The meaning stays on the label's title and, where a
// page asks for it, as one line under it.
export function CampaignStatus({
  status,
  showMeaning = false,
  mail,
}: {
  status: string
  showMeaning?: boolean
  mail?: MailSummary | null
}) {
  const key = statusKey(status)
  if (!key) {
    return <Status tone="neutral">{statusWords(status)}</Status>
  }
  const spec = STATUS[key]
  let meaning = spec.meaning
  let tone = spec.tone
  const outcome = key === 'APPROVED' || key === 'SENDING' || key === 'SENT' ? mailOutcome(mail) : 'none'
  const why = mail?.mail ? ' ' + whyNotEmailed(mail.mail) : ''
  if (outcome === 'all') {
    meaning = 'A second person approved it and the assessments were issued; nothing was emailed.' + why
    tone = 'warn'
  } else if (outcome === 'some') {
    meaning = 'A second person approved it; ' + mail?.notEmailed + ' of ' + mail?.issued + ' invitations were recorded, not emailed.' + why
    tone = 'warn'
  }
  return (
    <>
      <Status tone={tone} title={meaning}>
        {spec.label}
      </Status>
      {showMeaning && <span className="cell-sub">{meaning}</span>}
    </>
  )
}

// A recipient is quiet once the vendor has submitted, and loud only where the
// invitation did not arrive and somebody has to act.
const DELIVERY: Record<string, { label: string; tone: Tone; meaning: string }> = {
  PENDING: { label: 'Pending', tone: 'neutral', meaning: 'Nothing issued to this vendor yet.' },
  SENDING: { label: 'Sending', tone: 'info', meaning: 'The invitation is in the outbox.' },
  SENT: { label: 'Sent', tone: 'info', meaning: 'The invitation left the outbox.' },
  DELIVERED: { label: 'Delivered', tone: 'info', meaning: "The carrier confirmed the invitation reached the contact's phone." },
  READ: { label: 'Read', tone: 'info', meaning: 'The contact opened the message on their phone.' },
  OPENED: { label: 'Opened', tone: 'info', meaning: 'The vendor opened the invitation.' },
  STARTED: { label: 'Started', tone: 'info', meaning: 'The vendor started answering.' },
  SUBMITTED: { label: 'Submitted', tone: 'plain', meaning: 'The vendor submitted the questionnaire.' },
  SUPPRESSED: {
    label: 'Suppressed',
    tone: 'neutral',
    meaning: 'Held back on purpose. This vendor already had an open assessment, so a second one was not issued.',
  },
  BOUNCED: { label: 'Bounced', tone: 'crit', meaning: 'The address rejected the invitation.' },
  FAILED: { label: 'Failed', tone: 'crit', meaning: 'The invitation could not be queued or sent.' },
}

// A recipient's mail, MUTED or OFF, is an invitation recorded and not
// emailed. It is stored as FAILED, and is not a failure anybody can chase:
// it says what it is, in the warning tone, with why on its title.
export function DeliveryState({ state, mail }: { state: string; mail?: string | null }) {
  if (notEmailed(mail)) {
    return (
      <Status tone="warn" title={whyNotEmailed(mail ?? undefined) + ' Once mail is on, open the vendor and press ' + TASK_VERB.chase + ' on its next step.'}>
        Recorded, not emailed
      </Status>
    )
  }
  const spec = DELIVERY[(state || '').toUpperCase()]
  if (!spec) return <Status tone="neutral">{statusWords(state)}</Status>
  return (
    <Status tone={spec.tone} title={spec.meaning}>
      {spec.label}
    </Status>
  )
}

function isoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
}

function plusDays(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return isoDay(d)
}

// The create endpoint answers with more than the id: how many recipients it
// actually attached and which vendors it left out. A screen that reads only
// the id reports a campaign of three hundred when eleven had no address.
type CreateOutcome = {
  id: string
  recipients?: number
  skipped?: { vendorId: string; reason: string }[]
  note?: string
}

type FormError = { field: string; detail: string }

// The console routes on the fragment, so an in-page link written as href="#id"
// would be read as a route, fail to match one, and drop the reader on the
// portfolio with a half-filled form thrown away. The summary moves focus
// itself and leaves the fragment alone.
function focusField(id: string): void {
  const el = document.getElementById(id)
  if (!el) return
  el.scrollIntoView({ block: 'center' })
  el.focus()
}

const FIELDS: Record<string, { id: string; label: string }> = {
  name: { id: 'campaign-name', label: 'Campaign name' },
  questionnaireCode: { id: 'campaign-questionnaire', label: 'Questionnaire' },
  questionnaireVersionId: { id: 'campaign-questionnaire', label: 'Questionnaire' },
  riskModelCode: { id: 'campaign-model', label: 'Risk model' },
  riskModelVersionId: { id: 'campaign-model', label: 'Risk model' },
  mode: { id: 'campaign-mode', label: 'Mode' },
  dueOn: { id: 'campaign-due', label: 'Answer by' },
  vendorIds: { id: 'campaign-vendors', label: 'Who gets it' },
  recipients: { id: 'campaign-vendors', label: 'Who gets it' },
  reason: { id: 'campaign-reason', label: 'Reason' },
}

// What the "Raise a campaign" tab says while the list is the part on the page.
export function raiseTabMark(at: { onForm: boolean; refused: boolean; createdUnseen: boolean }): { alert?: string } {
  if (at.onForm) return {}
  if (at.refused) return { alert: 'not created, see why' }
  return at.createdUnseen ? { alert: 'created, see the note' } : {}
}

export function Campaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null)
  const [listMail, setListMail] = useState<string | undefined>(undefined)
  const [listError, setListError] = useState<unknown>(null)
  const [vendors, setVendors] = useState<Vendor[] | null>(null)
  const [vendorError, setVendorError] = useState<unknown>(null)

  const [name, setName] = useState('')
  const [questionnaire, setQuestionnaire] = useState('TPRM-BASELINE')
  const [questionnaireChoices, setQuestionnaireChoices] = useState(QUESTIONNAIRES)
  const [model, setModel] = useState('BASELINE')
  const [mode, setMode] = useState('FULL_CARRY_FORWARD')
  const [dueOn, setDueOn] = useState(() => plusDays(30))
  const [pickBy, setPickBy] = useState<'tier' | 'named'>('tier')
  const [tiers, setTiers] = useState<number[]>([])
  const [picked, setPicked] = useState<string[]>([])
  const [vendorSearch, setVendorSearch] = useState('')
  // Asked only when a suspended vendor is named (release 3.3).
  const [reason, setReason] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [formErrors, setFormErrors] = useState<FormError[]>([])
  // D11 of the date rules, said on the field once the person leaves it, and
  // again by the server when the campaign is approved.
  const leftDue = useLeftFields()
  const dueRule = checkDueAfterToday('Answer-by date', dueOn, todayIndia())
  const summaryRef = useRef<HTMLDivElement>(null)
  const [submitError, setSubmitError] = useState<unknown>(null)
  const [created, setCreated] = useState<CreateOutcome | null>(null)
  // The list first, the form on a second tab (10 Oct 2026): stacked, the form
  // nobody was filling in stood between the reader and the campaigns. What is
  // typed into it lives in this component, so it survives a look at the list.
  // A link can ask for the form (#/campaigns?part=raise), as the reassessment
  // triggers do: "Raise a campaign" should not land on the list.
  const [tab, setTab] = useState<'list' | 'raise'>(() =>
    new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('part') === 'raise' ? 'raise' : 'list',
  )
  // The note of what was created is news once. It is marked on the tab only
  // when it arrived while the reader was looking at the list, and until they
  // open the form again; a note already read is not marked on every return to
  // the list (review of 11 Oct 2026).
  const tabNow = useRef(tab)
  tabNow.current = tab
  const [createdUnseen, setCreatedUnseen] = useState(false)
  const openTab = (next: 'list' | 'raise') => {
    if (next === 'raise') setCreatedUnseen(false)
    setTab(next)
  }
  const tabsId = useId()
  const [focusTab, setFocusTab] = useState<'list' | 'raise' | null>(null)
  useEffect(() => {
    if (!focusTab) return
    document.getElementById(tabsId + focusTab)?.focus()
    setFocusTab(null)
  }, [focusTab, tabsId])

  const loadCampaigns = useCallback(() => {
    return api
      .campaigns()
      .then((r) => {
        setCampaigns(r.campaigns)
        setListMail(r.mail)
        setListError(null)
      })
      .catch((err) => {
        setCampaigns(null)
        setListError(err)
      })
  }, [])

  useEffect(() => {
    let live = true
    api
      .campaigns()
      .then((r) => {
        if (!live) return
        setCampaigns(r.campaigns)
        setListMail(r.mail)
        setListError(null)
      })
      .catch((err) => {
        if (live) setListError(err)
      })
    api
      .vendors({})
      .then((p) => {
        if (live) setVendors(p.vendors)
      })
      .catch((err) => {
        if (live) setVendorError(err)
      })
    api
      .questionnaires()
      .then((r) => {
        const published = (r.questionnaires ?? [])
          .filter((q) => q.publishedVersionId && !q.isArchived)
          .map((q) => ({ code: q.code, name: q.name }))
        if (!live || published.length === 0) return
        setQuestionnaireChoices(published)
        // Keep the choice when it is still offered, and otherwise start on the
        // first one offered, so the form never submits a set it did not show.
        setQuestionnaire((cur) => (published.some((q) => q.code === cur) ? cur : (published[0] as { code: string }).code))
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  // A rejected submit puts the reader on the list of what was wrong. It has to
  // wait for the render that fills the summary: focusing from inside the submit
  // handler reaches an element React has not written yet, and the first failed
  // attempt would silently leave focus on the button.
  useEffect(() => {
    if (formErrors.length > 0) summaryRef.current?.focus()
  }, [formErrors])

  const byId = useMemo(() => {
    const m = new Map<string, Vendor>()
    for (const v of vendors ?? []) m.set(v.id, v)
    return m
  }, [vendors])

  // Tier selection covers active vendors only. Inviting a vendor the business
  // has already offboarded is not a campaign, it is an apology. And only those
  // the server's gate takes (release 3.3, lib/requestRules.ts).
  const tierPool = useMemo(() => (vendors ?? []).filter((v) => v.status === 'ACTIVE' && mayPick(v)), [vendors])

  const untiered = useMemo(() => tierPool.filter((v) => !v.inherentTier).length, [tierPool])

  const selectedIds = useMemo(() => {
    if (pickBy === 'named') return picked
    return tierPool.filter((v) => v.inherentTier != null && tiers.includes(v.inherentTier)).map((v) => v.id)
  }, [pickBy, picked, tierPool, tiers])

  // Named vendors: one rule for every door (C5 of the 12 Oct plan). A vendor
  // whose request is not approved, one awaiting approval, and a rejected or
  // offboarded one are not offered; a suspended one is, with a reason.
  // Found the way the vendor picker finds one (kit/Picker.tsx): by the name
  // it trades under, the name on its papers or its code, the words in any
  // order. The owner is this list's own addition. It stays a list of ticks
  // and not a picker because a campaign names several vendors at once.
  const namedMatches = useMemo(() => {
    const all = (vendors ?? []).filter(mayPick)
    const found = new Set(
      pickerMatches(
        all.map((v) => ({
          value: v.id,
          label: v.displayName || v.legalName,
          code: v.vendorCode,
          also: [v.displayName, v.legalName, v.ownerUserId ?? ''],
        })),
        vendorSearch,
      ).map((o) => o.value),
    )
    return all.filter((v) => found.has(v.id))
  }, [vendors, vendorSearch])

  const holdingCount = useMemo(() => (campaigns ?? []).filter((c) => isHolding(c.status)).length, [campaigns])
  const holdingRows = useMemo(() => (campaigns ?? []).filter((c) => isHolding(c.status)), [campaigns])

  function toggleTier(t: number) {
    setTiers((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]))
  }

  function togglePicked(id: string) {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const reasonAsked = useMemo(
    () => pickNeedsReason((vendors ?? []).filter((v) => selectedIds.includes(v.id))),
    [vendors, selectedIds],
  )

  function validate(): FormError[] {
    const errs: FormError[] = []
    if (reasonAsked && !reason.trim()) {
      errs.push({ field: 'reason', detail: 'A suspended vendor is in this campaign. Say why it is sent due diligence.' })
    }
    if (!name.trim()) {
      errs.push({ field: 'name', detail: 'Give the campaign a name the approver will recognise.' })
    }
    if (!dueOn) {
      errs.push({ field: 'dueOn', detail: 'Set a due date, it appears in the vendor invitation' })
    } else if (dueRule) {
      errs.push({ field: 'dueOn', detail: dueRule })
    }
    if (selectedIds.length === 0) {
      errs.push({
        field: 'vendorIds',
        detail:
          pickBy === 'tier'
            ? 'Choose at least one tier. No active vendor is in scope yet.'
            : 'Select at least one vendor.',
      })
    }
    return errs
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    setCreated(null)
    const errs = validate()
    setFormErrors(errs)
    if (errs.length > 0) return

    setSubmitting(true)
    // The API takes vendor ids and nothing else, so a tier selection is
    // expanded to the exact list here. That is also what the approver needs to
    // see: the campaign records who was invited, not the rule that found them.
    api
      .createCampaign({
        name: name.trim(),
        questionnaireCode: questionnaire,
        riskModelCode: model,
        mode,
        dueOn,
        vendorIds: selectedIds,
        reason: reasonAsked ? reason.trim() : undefined,
      })
      .then((r) => {
        const outcome = r as CreateOutcome
        setCreated(outcome)
        setCreatedUnseen(tabNow.current !== 'raise')
        setName('')
        setPicked([])
        setTiers([])
        return loadCampaigns()
      })
      .catch((err) => {
        if (err instanceof ApiError && err.issues.length > 0) {
          setFormErrors(err.issues.map((i) => ({ field: i.field, detail: i.detail })))
        } else {
          setSubmitError(err)
        }
      })
      .finally(() => setSubmitting(false))
  }

  const columns: Column<Campaign>[] = [
    {
      key: 'name',
      header: 'Campaign',
      sortValue: (c) => c.name,
      render: (c) => (
        <>
          <a className="cell-primary" href={'#/campaign/' + encodeURIComponent(c.id)}>
            {c.name}
          </a>
          {isHolding(c.status) && <span className="cell-sub">Nothing sent for this one yet</span>}
        </>
      ),
    },
    {
      key: 'mode',
      header: 'Mode',
      sortValue: (c) => c.mode,
      render: (c) => {
        const m = MODES.find((x) => x.code === c.mode)
        return (
          <>
            {m ? m.label : c.mode.replace(/_/g, ' ')}
            {c.mode === 'FULL_CARRY_FORWARD' && <span className="cell-sub">prior answers pre-filled</span>}
            {c.mode === 'DELTA' && <span className="cell-sub">moved sections only</span>}
          </>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (c) => STATUS_RANK[c.status] ?? 0,
      render: (c) => {
        const mail = summariseCounts(c, listMail)
        const track = campaignStages(c.status, mail)
        return (
          <span className="sw-row">
            <CampaignStatus status={c.status} mail={mail} />
            {track && <MiniTrack states={track.states} label={track.label} />}
          </span>
        )
      },
    },
    {
      key: 'due',
      header: 'Due',
      sortValue: (c) => c.dueOn ?? null,
      render: (c) => (c.dueOn ? <span className="mono">{shortDate(c.dueOn)}</span> : <span className="muted">no date</span>),
    },
    {
      key: 'recipients',
      header: 'Recipients',
      numeric: true,
      sortValue: (c) => c.recipients ?? 0,
      render: (c) => <span className="mono">{c.recipients ?? 0}</span>,
    },
    {
      key: 'issued',
      header: 'Issued',
      numeric: true,
      sortValue: (c) => c.issued ?? 0,
      render: (c) =>
        (c.issued ?? 0) > 0 ? <span className="mono">{c.issued}</span> : <span className="muted">0</span>,
    },
    {
      key: 'suppressed',
      header: 'Suppressed',
      numeric: true,
      sortValue: (c) => c.suppressed ?? 0,
      render: (c) =>
        (c.suppressed ?? 0) > 0 ? (
          <span className="mono" title="Held back because the vendor already had an open assessment">
            {c.suppressed}
          </span>
        ) : (
          <span className="muted">0</span>
        ),
    },
    {
      key: 'createdBy',
      header: 'Raised by',
      sortValue: (c) => c.createdBy,
      render: (c) => (
        <>
          <span className="mono">{c.createdBy}</span>
          {c.approvedBy ? (
            <span className="cell-sub">
              approved by <span className="mono">{c.approvedBy}</span>
              {c.approvedAt ? ' on ' + shortDate(c.approvedAt) : ''}
            </span>
          ) : (
            <span className="cell-sub">no approver yet</span>
          )}
        </>
      ),
    },
  ]

  return (
    <>
      <AssessTabs at="campaigns" />
      <div className="p1-head">
        <div>
          <h1>Bulk assessments</h1>
        </div>
        <PageTabList
          id={tabsId}
          label="Bulk assessments"
          inline
          value={tab}
          onChange={openTab}
          tabs={[
            {
              key: 'list',
              label: 'Bulk assessments',
              ...(campaigns ? { count: campaigns.length } : {}),
              ...(listError != null ? { alert: 'could not be loaded' } : {}),
            },
            {
              key: 'raise',
              label: 'Raise a bulk assessment',
              // The form's answer can arrive after the reader has gone back to
              // the list. A refusal is marked for as long as it stands, since
              // the campaign is still not raised; the note of what was created
              // and who was left out is marked until they have looked at it.
              ...raiseTabMark({ onForm: tab === 'raise', refused: formErrors.length > 0 || submitError != null, createdUnseen: createdUnseen && created != null }),
            },
          ]}
        />
      </div>

      <div className={holdingCount > 0 ? 'note warn holding' : 'note holding'} role="status">
        <strong>
          {holdingCount > 0
            ? holdingCount + (holdingCount === 1 ? ' campaign is holding' : ' campaigns are holding')
            : 'No campaign is waiting on a decision'}
        </strong>
        {/* When nothing is holding the heading says so on its own; the line
            that used to repeat it underneath is gone. */}
        {holdingCount > 0 && (
          <>
            <p style={{ marginBottom: 6 }}>Waiting for a second person, nothing has been sent</p>
            <ul className="holding-list">
              {holdingRows.map((c) => (
                <li key={c.id}>
                  <a href={'#/campaign/' + encodeURIComponent(c.id)}>{c.name}</a>{' '}
                  <span className="muted">
                    {c.recipients ?? 0} recipient{(c.recipients ?? 0) === 1 ? '' : 's'}, raised by {c.createdBy}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <PageTabPanel id={tabsId} value={tab}>
      {/* Hidden, not unmounted, while the list is open: the form's error
          summary and the note that says what was created hold focus targets
          and a live region that must not be torn down mid-announcement. */}
      <section className="panel" aria-labelledby="create-heading" hidden={tab !== 'raise'}>
        <h2 id="create-heading">Raise a bulk assessment</h2>
        {/* Different vendors, different questionnaires: the bulk send that
            proposes each vendor's baseline (release 3.4, M7). */}
        <p className="sw-row bl-entry">
          <span className="muted">One questionnaire for every vendor below.</span>
          <a href="#/campaigns/new-batch">
            Send each vendor its baseline
          </a>
        </p>

        <div
          className="form-errors"
          ref={summaryRef}
          tabIndex={-1}
          role={formErrors.length > 0 ? 'alert' : undefined}
          hidden={formErrors.length === 0}
        >
          <h3>This campaign was not created</h3>
          <p>Fix the following, then press Create campaign again.</p>
          <ol>
            {formErrors.map((f, i) => {
              const target = FIELDS[f.field]
              return (
                <li key={f.field + i}>
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => focusField(target?.id ?? 'campaign-name')}
                  >
                    {target?.label ?? f.field}
                  </button>
                  : {f.detail}
                </li>
              )
            })}
          </ol>
        </div>

        {submitError != null && <ErrorBox error={submitError} what="The campaign" />}

        {created && (
          <div className="note" role="status">
            <strong>Created in draft, nothing has been sent</strong>
            {/* The count and what has not happened stay on the line; when it
                does happen is one press away. */}
            <p style={{ marginBottom: 6 }}>
              {created.recipients ?? selectedIds.length} recipient
              {(created.recipients ?? selectedIds.length) === 1 ? '' : 's'} attached, no assessment issued and no
              invitation queued{' '}
              <Toggletip label="When it goes out">That happens when a second person approves it.</Toggletip>
            </p>
            {created.skipped && created.skipped.length > 0 && (
              <>
                <p style={{ marginBottom: 4 }}>
                  <strong>{created.skipped.length} vendor left out:</strong>
                </p>
                <ul className="holding-list">
                  {created.skipped.map((s) => (
                    <li key={s.vendorId}>
                      {byId.get(s.vendorId)?.displayName || byId.get(s.vendorId)?.legalName || s.vendorId}:{' '}
                      <span className="muted">{s.reason}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <p style={{ margin: 0 }}>
              <a href={'#/campaign/' + encodeURIComponent(created.id)}>Open the campaign to review and approve it</a>
            </p>
          </div>
        )}

        <form onSubmit={submit} noValidate>
          <div className="form-row">
            <div className="field" style={{ flex: '2 1 260px' }}>
              <label htmlFor="campaign-name">Campaign name</label>
              <input
                id="campaign-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="2026 Q4 reassessment, critical vendors"
              />
            </div>

            <div className="field" style={{ flex: '1 1 200px' }}>
              <label htmlFor="campaign-due">Answer by</label>
              <input
                id="campaign-due"
                type="date"
                value={dueOn}
                min={plusDays(1)}
                onChange={(e) => setDueOn(e.target.value)}
                onBlur={() => leftDue.leave('dueOn')}
                aria-invalid={leftDue.shown('dueOn', dueRule) ? true : undefined}
                aria-describedby={leftDue.shown('dueOn', dueRule) ? 'campaign-due-rule' : undefined}
              />
              <DateRuleNote id="campaign-due-rule" message={leftDue.shown('dueOn', dueRule)} />
              <DateWarnNote id="campaign-due-warn" message={leftDue.shown('dueOn', dueRule ? '' : dueWarning(dueOn, '', todayIndia()))} />
            </div>
          </div>

          <div className="form-row">
            <div className="field" style={{ flex: '1 1 240px' }}>
              <label htmlFor="campaign-questionnaire">Questionnaire</label>
              <select id="campaign-questionnaire" value={questionnaire} onChange={(e) => setQuestionnaire(e.target.value)}>
                {questionnaireChoices.map((q) => (
                  <option key={q.code} value={q.code}>
                    {q.name ? q.code + ' · ' + q.name : q.code}
                  </option>
                ))}
              </select>
            </div>

            <div className="field" style={{ flex: '1 1 240px' }}>
              <label htmlFor="campaign-model">Risk model</label>
              <select id="campaign-model" value={model} onChange={(e) => setModel(e.target.value)}>
                {RISK_MODELS.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.code}
                  </option>
                ))}
              </select>
            </div>

            <div className="field" style={{ flex: '1 1 240px' }}>
              <label htmlFor="campaign-mode">Mode</label>
              <select id="campaign-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
                {MODES.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <fieldset className="picker" id="campaign-vendors" tabIndex={-1}>
            <legend>Who gets it</legend>

            {vendorError != null && <ErrorBox error={vendorError} what="The vendor list" />}
            {vendors === null && vendorError == null && <Loading what="the vendor list" />}

            {vendors !== null && (
              <>
                <div className="chiprow" style={{ marginBottom: 10 }}>
                  <label className="radio-inline">
                    <input
                      type="radio"
                      name="pick-by"
                      value="tier"
                      checked={pickBy === 'tier'}
                      onChange={() => setPickBy('tier')}
                    />
                    By tier
                  </label>
                  <label className="radio-inline">
                    <input
                      type="radio"
                      name="pick-by"
                      value="named"
                      checked={pickBy === 'named'}
                      onChange={() => setPickBy('named')}
                    />
                    By name
                  </label>
                </div>

                {pickBy === 'tier' ? (
                  <>
                    <div className="chiprow">
                      {TIERS.map((t) => {
                        const n = tierPool.filter((v) => v.inherentTier === t).length
                        return (
                          <button
                            key={t}
                            type="button"
                            className="chip-toggle"
                            aria-pressed={tiers.includes(t)}
                            onClick={() => toggleTier(t)}
                            disabled={n === 0}
                          >
                            {tierLabel(t)} ({n})
                          </button>
                        )
                      })}
                    </div>
                    {untiered > 0 && (
                      <p className="field-note">
                        {untiered} active vendor{untiered === 1 ? ' has' : 's have'} no inherent tier, so pick
                        {untiered === 1 ? ' it' : ' those'} by name
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="field" style={{ maxWidth: 320, marginBottom: 8 }}>
                      <label htmlFor="campaign-vendor-search">Find a vendor</label>
                      <input
                        id="campaign-vendor-search"
                        type="search"
                        value={vendorSearch}
                        placeholder="Name or vendor code"
                        onChange={(e) => setVendorSearch(e.target.value)}
                      />
                    </div>
                    <div className="pickbox">
                      {namedMatches.length === 0 ? (
                        <p className="muted" style={{ margin: 0, padding: 6 }}>
                          No vendor matches, clear the box to see all of them
                        </p>
                      ) : (
                        namedMatches.map((v) => (
                          <label key={v.id} className="pickrow">
                            <input type="checkbox" checked={picked.includes(v.id)} onChange={() => togglePicked(v.id)} />
                            <span>
                              {v.displayName || v.legalName}
                              <span className="cell-sub">
                                {v.vendorCode}
                                {v.inherentTier ? ', ' + tierLabel(v.inherentTier) : ', no tier'}
                                {v.status !== 'ACTIVE' ? ', ' + v.status.replace(/_/g, ' ').toLowerCase() : ''}
                                {needsReason(v) ? ', needs a reason' : ''}
                              </span>
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </>
                )}

                {reasonAsked && (
                  <div className="field" style={{ marginTop: 10 }}>
                    <label htmlFor="campaign-reason">Reason</label>
                    <textarea
                      id="campaign-reason"
                      rows={2}
                      maxLength={1000}
                      value={reason}
                      aria-describedby="campaign-reason-note"
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <p className="sw-help" id="campaign-reason-note">
                      A suspended vendor is in this campaign. Say why it is sent due diligence.
                    </p>
                  </div>
                )}
                <p className="summary-line" style={{ margin: '10px 0 0' }} role="status">
                  <strong>{selectedIds.length}</strong> vendor{selectedIds.length === 1 ? '' : 's'} in this campaign
                  {selectedIds.length > 0 && vendors.length > 0 && (
                    <> of {vendors.length} on file</>
                  )}
                </p>
              </>
            )}
          </fieldset>

          <div className="note stop" style={{ margin: '14px 0' }}>
            <strong>Creating this campaign sends nothing</strong>
            Written in draft, it waits for a second person to approve it
          </div>

          <button
            type="submit"
            className="primary"
            data-action="tprm.campaign,campaigns.a01"
            disabled={submitting || !can('assessment.plan')}
            title={can('assessment.plan') ? undefined : refusalReason('assessment.plan')}
          >
            {submitting ? 'Creating' : 'Create campaign'}
          </button>
          {/* The refusal names roles and can run long, so it opens from the (i)
              beside a short label rather than sitting under the button. */}
          {!can('assessment.plan') && (
            <p className="field-note sw-row">
              <Status tone="neutral">Read only</Status>
              <ReasonTip label="Why you cannot create a campaign" text={refusalReason('assessment.plan')} />
            </p>
          )}
        </form>
      </section>

      {tab === 'list' && (
      <section className="panel panel-flush" aria-label="Bulk assessments">

        {campaigns === null && listError == null && <Loading what="the campaign list" />}
        {listError != null && <ErrorBox error={listError} what="The campaign list" />}

        {campaigns && (
          <DataTable
            caption="Bulk assessments in this tenant, with mode, approval status, due date, recipient count, assessments issued, recipients suppressed and who raised it."
            columns={columns}
            rows={campaigns}
            rowKey={(c) => c.id}
            defaultSort={{ key: 'status', dir: 'desc' }}
            empty={{
              title: 'No campaign has been raised',
              detail: 'Raise one to ask a group of vendors the same thing at once',
            }}
            footer={
              <ListCount shown={campaigns.length} noun="campaigns">
                {holdingCount > 0 ? ', ' + holdingCount + ' awaiting approval' : ''}
              </ListCount>
            }
          />
        )}
        {campaigns && campaigns.length === 0 && (
          <p style={{ padding: '0 16px 16px' }}>
            <button
              type="button"
              className="primary"
              onClick={() => {
                openTab('raise')
                setFocusTab('raise')
              }}
            >
              Raise a campaign
            </button>
          </p>
        )}
      </section>
      )}
      </PageTabPanel>
    </>
  )
}
